import { ICON_LIMITS, IconImportError, encodeGlyph, iconName, imageTypeOf, importSvgIcon, type NewIcon } from '@realmery/realm-core';
import { ZipError, unzip } from '../export/unzip.ts';

/**
 * Reading icons to import (docs/PARITY-STEP-4-PLAN.md section 10), as the desktop's
 * `IconPackReader`: pictures (PNG, JPEG, WebP), plain SVGs, a folder of them, or a zip of
 * them. Every file that can't be used is reported with why, instead of stopping the rest.
 * A picture is told by its bytes and decoded by the browser before it's kept; an SVG is read
 * into a glyph by realm-core and its markup goes no further.
 */

export interface IconFile {
  readonly name: string;
  readonly data: Uint8Array;
}

export interface IconReading {
  readonly icons: NewIcon[];
  readonly problems: string[];
}

/** Decodes a picture, or throws if the browser can't. Kept apart so tests can stand in for it. */
export type PictureCheck = (data: Uint8Array, mimeType: string) => Promise<void>;

export const browserPictureCheck: PictureCheck = async (data, mimeType) => {
  const bitmap = await createImageBitmap(new Blob([data as Uint8Array<ArrayBuffer>], { type: mimeType }));
  bitmap.close();
};

const PICTURE_TYPES: Readonly<Record<string, string>> = { png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg', webp: 'image/webp' };

/** A pack's name from what's imported: a zip's or folder's name, else "Imported" (`suggestedPackName`). */
export function suggestedPackName(files: readonly { name: string }[], folder: string | null): string {
  if (folder) return iconName(folder);
  if (files.length === 1 && files[0]!.name.toLowerCase().endsWith('.zip')) return iconName(files[0]!.name);
  return 'Imported';
}

async function readOne(file: IconFile, pack: string, out: IconReading, check: PictureCheck): Promise<void> {
  const lower = file.name.toLowerCase();
  if (file.name.startsWith('.') || lower.endsWith('.txt') || lower.endsWith('.md') || lower.endsWith('.json')) return;
  const name = iconName(file.name);
  if (file.data.byteLength > ICON_LIMITS.bytes) {
    out.problems.push(`${file.name}: it's over 4 MB - make it smaller first (a few hundred pixels across is plenty).`);
    return;
  }
  if (lower.endsWith('.svg')) {
    try {
      const glyph = importSvgIcon(new TextDecoder().decode(file.data));
      out.icons.push({ pack, name, kind: 'GLYPH', mimeType: 'image/svg+xml', data: new TextEncoder().encode(encodeGlyph(glyph)) });
    } catch (e) {
      out.problems.push(`${file.name}: ${e instanceof IconImportError ? e.message : "it couldn't be read."}`);
    }
    return;
  }
  const extension = lower.slice(lower.lastIndexOf('.') + 1);
  const named = PICTURE_TYPES[extension];
  if (!named) {
    out.problems.push(`${file.name}: not a picture Realmery can use (PNG, JPEG, WebP, or SVG).`);
    return;
  }
  // Told by its bytes, not its name; a PNG called .jpg is still kept as a PNG.
  const type = imageTypeOf(file.data);
  if (type !== 'image/png' && type !== 'image/jpeg' && type !== 'image/webp') {
    out.problems.push(`${file.name}: the picture couldn't be read.`);
    return;
  }
  try {
    await check(file.data, type);
  } catch {
    out.problems.push(`${file.name}: the picture couldn't be read.`);
    return;
  }
  out.icons.push({ pack, name, kind: 'PICTURE', mimeType: type, data: file.data });
}

/** Reads files, and the files in zips among them, as one pack. */
export async function readIconFiles(files: readonly IconFile[], pack: string, check: PictureCheck = browserPictureCheck): Promise<IconReading> {
  const out: IconReading = { icons: [], problems: [] };
  for (const file of files) {
    if (!file.name.toLowerCase().endsWith('.zip')) {
      await readOne(file, pack, out, check);
      continue;
    }
    try {
      for (const entry of await unzip(file.data)) {
        if (entry.data) await readOne({ name: entry.name, data: entry.data }, pack, out, check);
        else if (!entry.name.startsWith('.')) out.problems.push(`${entry.name}: ${entry.problem ?? "it couldn't be read."}`);
      }
    } catch (e) {
      out.problems.push(`${file.name}: ${e instanceof ZipError ? e.message : "the pack couldn't be read."}`);
    }
  }
  return out;
}
